import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  viewChild,
} from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ActiveElement, ChartData, ChartEvent, ChartOptions } from 'chart.js';
import { withAlpha } from '../chart/chart.component';

export interface DonutChartSegment {
  label: string;
  value: number;
  color: string;
}

/**
 * A ring/donut chart for a small set of segments (e.g. online/offline, alarm
 * priority breakdown) — not a general-purpose charting component, use `xemex-chart`
 * for axis-based bar/line charts instead.
 *
 * Wraps Chart.js's `doughnut` controller directly via `ng2-charts`'s `BaseChartDirective`
 * — the same charting package `xemex-chart` uses — rather than composing `xemex-chart`
 * itself: that component's options are built around cartesian scales and an index-based
 * hover-sync service for line/bar series, neither of which applies to an arc chart with
 * no axes.
 *
 * Pass `centerLabel` for a 2-segment "percentage" donut; omit it for a plain N-segment
 * breakdown ring (e.g. priority breakdown).
 */
@Component({
  selector: 'xemex-donut-chart',
  standalone: true,
  imports: [BaseChartDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './donut-chart.component.html',
})
export class DonutChartComponent {
  segments = input.required<DonutChartSegment[]>();
  size = input<number>(140);
  /** Ring band thickness (outer radius minus inner radius), converted below to Chart.js's
   * `cutout` percentage of the outer radius. */
  thickness = input<number>(13);
  centerLabel = input<string>();
  /** Smaller muted line(s) under the centre label. */
  centerSubLabel = input<string>();
  /** Makes each segment hoverable/clickable (e.g. drill-through to a filtered list), same
   * pattern as the dashboard's priority-wedge chart. Off by default — most consumers are
   * purely decorative. */
  interactive = input<boolean>(false);
  /** Currently-hovered segment label(s), driven by the consumer so it can dim this ring in sync
   * with e.g. a legend rendered outside this component. An array highlights a group of
   * segments together. */
  hoveredLabel = input<string | string[] | null>(null);
  /** Shows Chart.js's default hover tooltip with the segment's numeric value. Off by
   * default, matching most consumers being purely decorative rings. */
  showTooltip = input<boolean>(false);

  segmentHover = output<string | null>();
  segmentClick = output<string>();

  private readonly outerR = 46;
  private readonly baseChart = viewChild(BaseChartDirective);

  // The label used to live inside the 100x100 SVG viewBox at font-size 16 (a 0.16 ratio to
  // the box), scaling with it automatically. Rendering it as an HTML overlay instead (for
  // reliable optical centering, unlike SVG dominant-baseline) means that scale has to be
  // reproduced explicitly.
  protected readonly centerLabelFontSize = computed(() => this.size() * 0.16);

  // Chart.js data-array indices (from chartHover/chartClick's `active[].index`) map straight
  // back into this filtered list, so hover/click lookups and chart data always agree.
  protected readonly visibleSegments = computed(() => this.segments().filter(s => s.value > 0));

  // Base colors only, deliberately NOT reactive to `hoveredLabel` — feeding hover state through
  // this `[data]`-bound signal would hand Chart.js a brand-new `data` object on every hover, and
  // ng2-charts applies that via a full `chart.update()`, which replays the doughnut's default
  // grow-in (`animateRotate`) animation on every hover. The hover-driven recolor below applies
  // imperatively instead, via `chart.update('none')` — the same no-animation update
  // `ChartSyncService` uses for cross-chart hover sync.
  protected readonly chartData = computed<ChartData<'doughnut'>>(() => ({
    labels: this.visibleSegments().map(s => s.label),
    datasets: [
      {
        data: this.visibleSegments().map(s => s.value),
        backgroundColor: this.visibleSegments().map(s => s.color),
        hoverBackgroundColor: this.visibleSegments().map(s => s.color),
        borderWidth: 0,
        hoverBorderWidth: 0,
      },
    ],
  }));

  protected readonly chartOptions = computed<ChartOptions<'doughnut'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: `${Math.max(0, ((this.outerR - this.thickness()) / this.outerR) * 100)}%`,
    // Fully disable hit-testing when non-interactive, matching the old plain-SVG behavior
    // of never attaching pointer handlers at all.
    events: this.interactive() ? undefined : [],
    plugins: {
      legend: { display: false },
      tooltip: { enabled: this.showTooltip() },
    },
  }));

  constructor() {
    effect(() => {
      const hoveredLabel = this.hoveredLabel();
      const segments = this.visibleSegments();
      const chart = this.baseChart()?.chart;
      if (!chart) return;
      const hoveredLabels = hoveredLabel == null ? [] : [hoveredLabel].flat();
      const colors = segments.map(s =>
        hoveredLabels.length && !hoveredLabels.includes(s.label)
          ? withAlpha(s.color, 0.4)
          : s.color,
      );
      const dataset = chart.data.datasets[0];
      dataset.backgroundColor = colors;
      dataset.hoverBackgroundColor = colors;
      chart.update('none');
    });
  }

  protected onChartHover({ active }: { event: ChartEvent; active: object[] }): void {
    if (!this.interactive()) return;
    const index = (active as ActiveElement[])[0]?.index;
    this.segmentHover.emit(index != null ? this.visibleSegments()[index].label : null);
  }

  protected onChartClick({ active }: { event?: ChartEvent; active?: object[] }): void {
    if (!this.interactive()) return;
    const index = (active as ActiveElement[] | undefined)?.[0]?.index;
    if (index != null) this.segmentClick.emit(this.visibleSegments()[index].label);
  }
}
